import React, { FC, useMemo } from 'react';
import { Link as ReactLink } from 'react-router';

import moment from 'moment';

import Typography from '@mui/material/Typography';

import { useMediaQuery } from '@hooks/common/useMediaQuery';

import footerLogo from '@assets/logos/logo.png';

import Button from '@components/common/Button';
import Container from '@components/common/Container';
import Link from '@components/common/Link';

import {
  FOOTER_DESCRIPTION,
  QUICK_LINKS,
  QUICK_LINKS_MOB,
  SOCIAL_LINKS,
} from '@constants/common/footer.constants';
import {
  PATH_INDEX,
  PATH_TERMS_AND_CONDITIONS,
} from '@constants/common/routes.constants';
import { LinkVariant, Size, Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type Link = {
  title: string;
  href: string;
  isDisable?: boolean;
  newTab?: boolean;
};

const CURRENT_YEAR = moment().year();

const Footer: FC = () => {
  const { classes } = useStyles();

  const { isLaptop, mobileView } = useMediaQuery();

  const quickLinks: Link[][] = useMemo(
    () => (isLaptop || mobileView ? QUICK_LINKS_MOB : QUICK_LINKS),
    [isLaptop, mobileView],
  );

  return (
    <footer className={classes.footer}>
      <div className={classes.bg} />
      <Container>
        <div className={classes.box}>
          <div className={classes.left}>
            <ReactLink to={PATH_INDEX}>
              <img
                src={footerLogo}
                className={classes.logo}
                alt="logo dexFinance"
              />
            </ReactLink>
            <div className={classes.content}>
              <Typography variant="body1">{FOOTER_DESCRIPTION}</Typography>
            </div>
            <div className={classes.copyright}>
              <Typography variant="caption1">
                Copyright © {CURRENT_YEAR}&nbsp;
                <ReactLink className={classes.link} to={PATH_INDEX}>
                  dexfinance.com
                </ReactLink>
              </Typography>
              <Typography variant="caption1">All Rights Reserved.</Typography>
            </div>
          </div>

          <div className={classes.center}>
            <Typography variant="subtitle1">QUICK LINKS</Typography>
            <div className={classes.linksBox}>
              {quickLinks.map((routes, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <ul key={`key_${index}`} className={classes.list}>
                  {routes.map((item) => (
                    <Link
                      {...item}
                      key={item.title}
                      variant={LinkVariant.Light}
                    />
                  ))}
                </ul>
              ))}
            </div>
          </div>
          <div className={classes.right}>
            <Typography variant="subtitle1">LET’S GET SOCIAL</Typography>
            <Typography variant="subtitle1">
              Stay up with DexFi’s official social profiles and educational
              resources
            </Typography>
            <div className={classes.footerSocials}>
              {SOCIAL_LINKS.map(({ title, icon, path, disabled }) => (
                <Button
                  key={`${title}-footer`}
                  variant={Variant.Secondary}
                  size={Size.Large}
                  href={path}
                  iconRight={icon}
                  disabled={disabled}
                  className={classes.socialButton}
                  newTab
                >
                  {title}
                </Button>
              ))}
            </div>
            <div className={classes.terms}>
              <Typography variant="body2" className={classes.terms}>
                By using DexFi, you agree to the
              </Typography>
              <Link
                href={PATH_TERMS_AND_CONDITIONS}
                title="Terms & Conditions"
              />
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
